import React, { useState, useEffect } from 'react';
import TreeList, {
  Column,
  Paging,
  SearchPanel,
} from 'devextreme-react/tree-list';
import DateBox from 'devextreme-react/date-box';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../pnl/PnLTreeList.css';

import { formatNegativeValue } from '../../utils/formatUtils';

interface TreeDataRow {
  id: number | string;
  parentId: number | string | null;
  name: string;
  level?: number;
  __level?: number;
  expected_tail_loss: number;
  component_etl_to_rms: number;
  standalone_var: number;
  standalone_var_change_prev_day: number;
  standalone_var_change_prev_month: number;
  diversification_benefit: number;
  component_var_to_rms: number;
  component_var: number;
  incremental_var_to_rms: number;
  stressed_etl: number;
  stressed_var: number;
  risk_limit: number;
  risk_utilisation: number;
  avg_risk_utilisation_fytd: number;
  p50_risk_utilisation_fytd: number;
  max_risk_utilisation_fytd: number;
  [key: string]: any;
}

interface ExportRow {
  name: string;
  expected_tail_loss: number | null;
  component_etl_to_rms: number | null;
  standalone_var: number | null;
  standalone_var_change_prev_day: number | null;
  standalone_var_change_prev_month: number | null;
  diversification_benefit: number | null;
  component_var_to_rms: number | null;
  component_var: number | null;
  incremental_var_to_rms: number | null;
  stressed_etl: number | null;
  stressed_var: number | null;
  risk_limit: number | null;
  risk_utilisation: number | null;
  avg_risk_utilisation_fytd: number | null;
  p50_risk_utilisation_fytd: number | null;
  max_risk_utilisation_fytd: number | null;
}

interface MarketRiskTreeProps {
  page?: string;
  treeDataRisk: TreeDataRow[];
}

const MarketRiskTree: React.FC<MarketRiskTreeProps> = ({ page, treeDataRisk }) => {

  useEffect(() => {
    const interval = setInterval(() => {
      const scrollable = document.querySelector('.dx-treelist .dx-scrollable .dx-scrollable-container') as HTMLElement | null;
      if (!scrollable) return;

      let isDown = false;
      let startX: number;
      let scrollLeft: number;

      const handleMouseDown = (e: MouseEvent) => {
        isDown = true;
        scrollable.classList.add('scrolling-active');
        startX = e.pageX;
        scrollLeft = scrollable.scrollLeft;
      };

      const handleMouseUp = () => {
        isDown = false;
        scrollable.classList.remove('scrolling-active');
      };

      const handleMouseLeave = () => {
        isDown = false;
        scrollable.classList.remove('scrolling-active');
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX;
        const walk = (x - startX) * 1.5;
        scrollable.scrollLeft = scrollLeft - walk;
      };

      scrollable.addEventListener('mousedown', handleMouseDown);
      scrollable.addEventListener('mouseup', handleMouseUp);
      scrollable.addEventListener('mouseleave', handleMouseLeave);
      scrollable.addEventListener('mousemove', handleMouseMove);

      clearInterval(interval);
    }, 300);

    return () => clearInterval(interval);
  }, []);

  const renderFirstColumnHeader = () => {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onMouseLeave={(e) => e.stopPropagation()}
      >
        {/*<div style={{ fontSize: '12px', fontWeight: 'normal', color: '#666' }}>*/}
        {/*  COB Date:*/}
        {/*</div>*/}
        {/*<DateBox*/}
        {/*  type="date"*/}
        {/*  value={cobDate}*/}
        {/*  onValueChanged={(e) => setCobDate(e.value)}*/}
        {/*  placeholder="Select COB Date"*/}
        {/*  displayFormat="dd/MM/yyyy"*/}
        {/*  width={140}*/}
        {/*  height={28}*/}
        {/*  stylingMode="outlined"  elementAttr={{ class: 'datebox--sm' }}*/}
        {/*/>*/}
      </div>
    );
  };

  const isNum = (v: any): boolean => v !== null && v !== undefined && Number.isFinite(Number(v));
const fmt0 = (v: any): string => (isNum(v) ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "");
const fmtPct0 = (v: any): string => (isNum(v) ? `${Math.round(Number(v))}%` : "");

const levelIndent = (row: any): string => {
  const lvl = Number(row?.level ?? row?.__level ?? 0);
  return "  ".repeat(Math.max(0, lvl));
};

// This matches your TreeList calculateCellValue logic
const toExportRow = (row: TreeDataRow): ExportRow => {
  const name = `${levelIndent(row)}${row?.name ?? ""}`;

  return {
    name,

    expected_tail_loss: isNum(row.expected_tail_loss) ? row.expected_tail_loss / 1000 : null,
    component_etl_to_rms: isNum(row.component_etl_to_rms) ? row.component_etl_to_rms / 1000 : null,

    standalone_var: isNum(row.standalone_var) ? row.standalone_var / 1000 : null,
    standalone_var_change_prev_day: isNum(row.standalone_var_change_prev_day) ? row.standalone_var_change_prev_day / 1000 : null,
    standalone_var_change_prev_month: isNum(row.standalone_var_change_prev_month) ? row.standalone_var_change_prev_month / 1000 : null,

    diversification_benefit: isNum(row.diversification_benefit) ? row.diversification_benefit * 100 : null,

    component_var_to_rms: isNum(row.component_var_to_rms) ? row.component_var_to_rms / 1000 : null,
    component_var: isNum(row.component_var) ? Math.round(row.component_var * 100) : null,

    incremental_var_to_rms: isNum(row.incremental_var_to_rms) ? row.incremental_var_to_rms / 1000 : null,
    stressed_etl: isNum(row.stressed_etl) ? row.stressed_etl / 1000 : null,
    stressed_var: isNum(row.stressed_var) ? row.stressed_var / 1000 : null,

    risk_limit: isNum(row.risk_limit) ? row.risk_limit / 1000 : null,
    risk_utilisation: isNum(row.risk_utilisation) ? Math.round(row.risk_utilisation * 100) : null,

    avg_risk_utilisation_fytd: isNum(row.avg_risk_utilisation_fytd) ? row.avg_risk_utilisation_fytd * 100 : null,
    p50_risk_utilisation_fytd: isNum(row.p50_risk_utilisation_fytd) ? row.p50_risk_utilisation_fytd * 100 : null,
    max_risk_utilisation_fytd: isNum(row.max_risk_utilisation_fytd) ? row.max_risk_utilisation_fytd * 100 : null,
  };
};


  const handleExcelExport = async () => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet("Risk Report");

  worksheet.columns = [
    { header: "Name", key: "name", width: 38 },

    { header: "Expected Tail Loss (€'000s)", key: "expected_tail_loss", width: 20 },
    { header: "Component ETL to RMS (€'000s)", key: "component_etl_to_rms", width: 22 },

    { header: "Standalone VaR (€'000s)", key: "standalone_var", width: 18 },
    { header: "Daily change (€'000s)", key: "standalone_var_change_prev_day", width: 18 },
    { header: "MTD change (€'000s)", key: "standalone_var_change_prev_month", width: 18 },

    { header: "Diversification benefit (%)", key: "diversification_benefit", width: 22 },

    { header: "Component VaR to RMS (€'000s)", key: "component_var_to_rms", width: 22 },
    { header: "Component VaR (%)", key: "component_var", width: 16 },

    { header: "Incremental VaR to RMS (€'000s)", key: "incremental_var_to_rms", width: 26 },
    { header: "Stressed ETL (€'000s)", key: "stressed_etl", width: 18 },
    { header: "Stressed VaR (€'000s)", key: "stressed_var", width: 18 },

    { header: "Risk Limit (€'000s)", key: "risk_limit", width: 18 },
    { header: "Risk Limit Utilisation (%)", key: "risk_utilisation", width: 24 },

    { header: "FYTD Mean (%)", key: "avg_risk_utilisation_fytd", width: 14 },
    { header: "FYTD Median (%)", key: "p50_risk_utilisation_fytd", width: 16 },
    { header: "FYTD Max (%)", key: "max_risk_utilisation_fytd", width: 14 },
  ];

  const rows = treeDataRisk.map(toExportRow);
  rows.forEach((r) => worksheet.addRow(r));

  // Format numeric columns
  const numCols0dp = [
    "expected_tail_loss",
    "component_etl_to_rms",
    "standalone_var",
    "standalone_var_change_prev_day",
    "standalone_var_change_prev_month",
    "component_var_to_rms",
    "incremental_var_to_rms",
    "stressed_etl",
    "stressed_var",
    "risk_limit",
  ];
  numCols0dp.forEach((k) => {
    worksheet.getColumn(k).numFmt = "#,##0";
  });

  const pctCols0dp = [
    "diversification_benefit",
    "component_var",
    "risk_utilisation",
    "avg_risk_utilisation_fytd",
    "p50_risk_utilisation_fytd",
    "max_risk_utilisation_fytd",
  ];
  pctCols0dp.forEach((k) => {
    worksheet.getColumn(k).numFmt = '0"%"';
  });

  // Header style
  worksheet.getRow(1).font = { bold: true };
  worksheet.views = [{ state: "frozen" as const, ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  saveAs(new Blob([buffer], { type: "application/octet-stream" }), `Market Risk Report ${today}.xlsx`);
};


  const handlePdfExport = () => {
  const doc = new jsPDF({ orientation: "landscape" });

  const head = [[
    "Name",
    "ETL (€'000s)",
    "Comp ETL (€'000s)",
    "Standalone VaR (€'000s)",
    "Daily chg (€'000s)",
    "MTD chg (€'000s)",
    "Div benefit (%)",
    "Comp VaR to RMS (€'000s)",
    "Comp VaR (%)",
    "Incr VaR to RMS (€'000s)",
    "Stressed ETL (€'000s)",
    "Stressed VaR (€'000s)",
    "Risk Limit (€'000s)",
    "Util (%)",
    "FYTD Mean (%)",
    "FYTD Med (%)",
    "FYTD Max (%)",
  ]];

  const body = treeDataRisk.map((row) => {
    const r = toExportRow(row);
    return [
      r.name,
      fmt0(r.expected_tail_loss),
      fmt0(r.component_etl_to_rms),
      fmt0(r.standalone_var),
      fmt0(r.standalone_var_change_prev_day),
      fmt0(r.standalone_var_change_prev_month),
      fmtPct0(r.diversification_benefit),
      fmt0(r.component_var_to_rms),
      fmtPct0(r.component_var),
      fmt0(r.incremental_var_to_rms),
      fmt0(r.stressed_etl),
      fmt0(r.stressed_var),
      fmt0(r.risk_limit),
      fmtPct0(r.risk_utilisation),
      fmtPct0(r.avg_risk_utilisation_fytd),
      fmtPct0(r.p50_risk_utilisation_fytd),
      fmtPct0(r.max_risk_utilisation_fytd),
    ];
  });

  (doc as any).autoTable({
    head,
    body,
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fontStyle: "bold" },
    margin: { top: 10, left: 6, right: 6 },
    tableWidth: "auto",
  });
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    doc.save(`Market Risk Report ${today}.pdf`);

};


  return (
    <div className="tree-container">
        <div style={{ display: 'flex', justifyContent: 'right', gap: '12px', marginBottom: '20px' }}>
        <button
          onClick={handleExcelExport}
          style={{
            padding: '10px 18px',
            borderRadius: '4px',
            backgroundColor: '#28a745', // green
            color: 'white',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Export to Excel
        </button>
        <button
          onClick={handlePdfExport}
          style={{
            padding: '10px 18px',
            borderRadius: '4px',
            backgroundColor: '#d9534f', // red
            color: 'white',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Export to PDF
        </button>
      </div>
      <TreeList
          elementAttr={{ class: 'treelist-dense' }}
        dataSource={treeDataRisk}
        keyExpr="id"
        parentIdExpr="parentId"
        showBorders={true}
        //autoExpandAll={true}
        columnAutoWidth={true}
       //height={700}
          //width={2000}
        scrolling={{
          mode: 'virtual',
          //useNative: false,
          showScrollbar: 'always',
        }}
        headerFilter={{ visible: true }}
        onContentReady={(e: any) => {
          const grid = e.component;
// 1) Expand all roots
  const roots = grid.getVisibleRows().filter((r: any) => r.level === 0);
  roots.forEach((r: any) => grid.expandRow(r.key));

  // 2) After roots render, expand their first child only
  setTimeout(() => {
    roots.forEach((r: any) => {
      const node = grid.getNodeByKey(r.key);
      const firstChild = node?.children?.[0];
      if (firstChild) grid.expandRow(firstChild.key);
    });
  }, 0);
}}
      >
        {/*<SearchPanel visible={true} />*/}
        <Paging enabled={false} />

        <Column
          dataField="name"
          caption=""
          width={280}
          headerCellRender={renderFirstColumnHeader}
          fixed={true}
          fixedPosition="left"
          allowSorting={false}
        />

        <Column
          dataField="expected_tail_loss"
          headerCellRender={() => (
            <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center", fontSize: "11px"}}>
              Expected Tail Loss<br/>(1-day 95%)
            </div>
          )}
          //width={100}
          calculateCellValue={(rowData: any) => rowData.expected_tail_loss / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />
           <Column
          dataField="component_etl_to_rms"
          headerCellRender={() => (
            <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center", fontSize: "11px"}}>
              Component<br/>ETL to RMS
            </div>
          )}
          //width={100}
          calculateCellValue={(rowData: any) => rowData.component_etl_to_rms / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />

        <Column
          dataField="standalone_var"
          headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      Standalone<br/> Var <br/>(1-day 95%)
    </div>)}
          //width={100}
          calculateCellValue={(rowData: any) => rowData.standalone_var / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />

                  <Column
          dataField="standalone_var_change_prev_day"
          headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      Daily change<br/> (€'000s)
    </div>)}
          //width={100}
          calculateCellValue={(rowData: any) => rowData.standalone_var_change_prev_day / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />
                  <Column
          dataField="standalone_var_change_prev_month"
          headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      MTD change<br/> (€'000s)
    </div>)}
          //width={100}
          calculateCellValue={(rowData: any) => rowData.standalone_var_change_prev_month / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />
        <Column
              dataField="diversification_benefit"
              headerCellRender={() => (
        <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
          Diversification<br/> benefit
        </div>)}

              //width={80}
              calculateCellValue={(rowData: any) => rowData.diversification_benefit *100}
              cellRender={({ value }: any) => formatNegativeValue(value,{isPercent:true,decimals:0})}
            />

          <Column
          dataField="component_var_to_rms"
          headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      Component <br/> VaR to RMS
    </div>)}
          //width={100}
          calculateCellValue={(rowData: any) => rowData.component_var_to_rms / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />

                    <Column
          dataField="component_var"
          headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      Component <br/> VaR (%)
    </div>)}
          //width={100}
          calculateCellValue={(rowData: any) => Math.round(rowData.component_var *100)}
          cellRender={({ value }: any) => formatNegativeValue(value,{isPercent:true,decimals:0})}
        />


        <Column
          dataField="incremental_var_to_rms"
          headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center", fontSize: "11px" }}>
      Incremental<br/>Var to RMS<br/>(€'000s)
    </div>)}

          //width={80}
          calculateCellValue={(rowData: any) => rowData.incremental_var_to_rms / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />
          <Column
          dataField="stressed_etl"
          headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center", fontSize: "11px" }}>
      Stressed ETL<br/>(€'000s)
    </div>)}

          //width={80}
          calculateCellValue={(rowData: any) => rowData.stressed_etl / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />
        <Column
          dataField="stressed_var"
                    headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      Stressed<br/> Var <br/>(€'000s)
    </div>)}

          //width={80}
          calculateCellValue={(rowData: any) => rowData.stressed_var / 1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />


        <Column
          dataField="risk_limit"
          caption="Risk Limit"
    headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center", fontSize: "11px" }}>
      Risk Limit<br/>(€'000s)
    </div>)}
          //width={80}
          calculateCellValue={(rowData: any) => rowData.risk_limit/1000}
          cellRender={({ value }: any) => formatNegativeValue(value)}
        />
        <Column
          dataField="risk_utilisation"
            headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      Risk Limit <br/>Utilisation
    </div>)}

          //width={80}
          calculateCellValue={(rowData: any) => Math.round(rowData.risk_utilisation *100)}
          cellRender={({ value }: any) => formatNegativeValue(value,{isPercent:true,decimals:0})}
        />
        <Column caption="Risk Limit Utilisation" alignment="center">
        <Column
          dataField="avg_risk_utilisation_fytd"
            headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      FYTD Mean
    </div>)}

          calculateCellValue={(rowData: any) => rowData.avg_risk_utilisation_fytd*100 }
          cellRender={({ value }: any) => formatNegativeValue(value,{isPercent:true,decimals:0})}
        />
        <Column
          dataField="p50_risk_utilisation_fytd"
            headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center" , fontSize: "11px"}}>
      FYTD Median
    </div>)}

          calculateCellValue={(rowData: any) => rowData.p50_risk_utilisation_fytd*100}
          cellRender={({ value }: any) => formatNegativeValue(value,{isPercent:true,decimals:0})}
        />
        <Column
          dataField="max_risk_utilisation_fytd"
            headerCellRender={() => (
    <div style={{ whiteSpace: "normal", lineHeight: "16px", textAlign: "center", fontSize: "11px"}}>
      FYTD Max
    </div>)}
          calculateCellValue={(rowData: any) => rowData.max_risk_utilisation_fytd*100 }
          cellRender={({ value }: any) => formatNegativeValue(value,{isPercent:true,decimals:0})}
        />
          </Column>


      </TreeList>


    </div>
  );
};

export default MarketRiskTree;
